import { useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import "dayjs/locale/it";
import { InputCst } from "@components/InputCst";
import { useTranslation } from "react-i18next";
import {
  AddButton,
  Divider,
  MainContainer,
  SectionTitle,
  SingleSlotContainer,
  SingleSlotContent,
  SlotsGridItem,
} from "@components/TimeSlotCst/style";

export interface Description {
  id?: number | null | undefined;
  title?: string;
  deletable?: boolean;
}

interface DescriptionCstProps {
  initialDescriptionSlots: Description[];
  onChange: (newRange: Description[]) => void;
  error: boolean;
  errorMessage: string;
  maxValue: number;
  maxDescription: number;
  multiline: boolean;
  rows: number;
  translationKey: string;
}

const DescriptionCst = ({
  initialDescriptionSlots,
  onChange,
  error,
  errorMessage,
  maxValue,
  maxDescription,
  multiline,
  rows,
  translationKey,
}: DescriptionCstProps) => {
  const { t } = useTranslation(translationKey);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [descriptionSlots, setDescriptionSlots] = useState<Description[]>(
    initialDescriptionSlots || [],
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [tempDescription, setTempDescription] = useState<string>("");
  const [descriptionSlotData, setDescriptionSlotData] = useState<Description>({
    title: "",
    deletable: true,
  });

  const addSlot = () => {
    if (!validateFields()) return;
    const updatedRangeList = [
      ...descriptionSlots,
      {
        ...descriptionSlotData,
        id: null,
        title: descriptionSlotData.title,
        deletable: descriptionSlotData.deletable,
      },
    ];

    setDescriptionSlotData({
      id: null,
      title: "",
      deletable: true,
    });

    setDescriptionSlots(updatedRangeList);
    onChange(updatedRangeList);
  };

  const delRange = (selectedIndex: number) => {
    const updatedRangeList = [...descriptionSlots];
    updatedRangeList.splice(selectedIndex, 1);
    setDescriptionSlots(updatedRangeList);
    onChange(updatedRangeList);
  };

  const editRange = (index: number) => {
    setEditingIndex(index);
    setTempDescription(descriptionSlots[index].title || "");
  };

  const saveEdit = (index: number) => {
    const updatedSlots = [...descriptionSlots];
    updatedSlots[index].title = tempDescription;
    setDescriptionSlots(updatedSlots);
    setEditingIndex(null);
    onChange(updatedSlots);
  };

  const validateFields = () => {
    setValidationErrors([]);
    const missingFieldKeys: string[] = [];
    if (!descriptionSlotData.title) missingFieldKeys.push("description");

    setValidationErrors(missingFieldKeys);
    return !missingFieldKeys || missingFieldKeys.length === 0;
  };

  return (
    <Grid item xs={12}>
      <MainContainer theme={theme} sx={{ mt: 0 }}>
        <SectionTitle theme={theme}>{t("descriptions.title")}</SectionTitle>
        <Grid container spacing={"24px"}>
          {Object.keys(descriptionSlots).length < maxDescription && (
            <Grid item xs={12}>
              <InputCst
                label={t("descriptions.description") || ""}
                value={descriptionSlotData.title}
                required={true}
                disabled={false}
                onChange={(value: any) => {
                  setDescriptionSlotData((current) => ({
                    ...current,
                    title: value.target.value,
                  }));
                }}
                rows={rows}
                multiline={multiline}
                maxValue={maxValue}
                type={"text"}
              />

              {error && (
                <Typography
                  color={theme.palette.error.main}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {error ? (errorMessage ? errorMessage : null) : null}
                </Typography>
              )}
            </Grid>
          )}

          {Object.keys(descriptionSlots).length < maxDescription && (
            <Grid item xs={12}>
              <AddButton theme={theme} onClick={addSlot}>
                <Add color={"primary"} />
                <Typography sx={{ color: theme.palette.primary.main }}>
                  {t("descriptions.add-slot-btn")}
                </Typography>
              </AddButton>
            </Grid>
          )}

          {descriptionSlots && descriptionSlots.length > 0 && (
            <Grid item xs={12}>
              <SlotsGridItem gap={2}>
                <Divider theme={theme} />

                {descriptionSlots.map((range, rangeIndex) => {
                  return (
                    <SingleSlotContainer
                      theme={theme}
                      key={`range_${rangeIndex}_${range.title}`}
                    >
                      {!isSmallScreen && (
                        <>
                          <IconButton
                            color={"primary"}
                            onClick={() => delRange(rangeIndex)}
                            disabled={
                              range.deletable !== undefined
                                ? !range.deletable
                                : false
                            }
                          >
                            <Delete />
                          </IconButton>
                          <IconButton
                            color={"primary"}
                            onClick={() => editRange(rangeIndex)}
                          >
                            <Edit />
                          </IconButton>
                        </>
                      )}
                      <SingleSlotContent
                        gap={isSmallScreen ? "10px" : "5px"}
                        theme={theme}
                      >
                        {editingIndex === rangeIndex ? (
                          <InputCst
                            value={tempDescription}
                            onChange={(e: any) =>
                              setTempDescription(e.target.value)
                            }
                            onBlur={() => saveEdit(rangeIndex)}
                            autoFocus
                            rows={rows}
                            multiline={multiline}
                            maxValue={maxValue}
                          />
                        ) : (
                          <Typography>{range.title}</Typography>
                        )}
                        {isSmallScreen && (
                          <>
                            <Button
                              onClick={() => delRange(rangeIndex)}
                              startIcon={<Delete />}
                              color={"primary"}
                              variant={"contained"}
                              disabled={
                                range.deletable !== null
                                  ? !range.deletable
                                  : false
                              }
                            >
                              {t("descriptions.delete-slot-btn")}
                            </Button>
                            <Button
                              onClick={() => editRange(rangeIndex)}
                              startIcon={<Edit />}
                              color={"primary"}
                              variant={"contained"}
                            >
                              {t("descriptions.edit-slot-btn")}
                            </Button>
                          </>
                        )}
                      </SingleSlotContent>
                    </SingleSlotContainer>
                  );
                })}
              </SlotsGridItem>
            </Grid>
          )}
        </Grid>
      </MainContainer>
    </Grid>
  );
};

export default DescriptionCst;
